import { api } from './client';

export interface SerpRequest {
  request_id: string;
  target_keyword: string;
  target_url?: string;
  location_code: number;
  language_code: string;
  status: string;
  serp_results_count?: number;
  cost_dataforseo?: number;
  project_id?: string;
  dataforseo_profile_key?: 'dataforseo_personal' | 'dataforseo_company' | null;
  dataforseo_account?: 'personal' | 'company' | null;
  created_at: string;
  completed_at?: string;
  cached_at?: string;
}

export interface SerpResult {
  result_id: string;
  request_id: string;
  rank_absolute: number;
  rank_group: number;
  domain: string;
  title: string;
  url: string;
  snippet?: string;
  item_type: string;
  scraping_task_id?: string;
  url_registry_id?: string;
}

export interface SerpRequestWithResults {
  request: SerpRequest;
  results: SerpResult[];
}

export interface SerpStats {
  total_requests: number;
  completed: number;
  pending: number;
  total_results: number;
  total_cost: number;
}

export const serpApi = {
  status: () =>
    api.get<{ configured: boolean; balance?: number }>('/serp/status'),

  stats: () =>
    api.get<SerpStats>('/serp/stats'),

  analyze: (data: {
    keyword: string;
    location_code?: number;
    language_code?: string;
    project_id?: string;
    target_url?: string;
    force_refresh?: boolean;
    auto_scrape?: boolean;
    analysis_account_profile_key?: 'personal' | 'company' | null;
    tag?: string;
    selected_stages?: string[];
  }) => api.post('/serp/analyze', data),

  batchAnalyze: (data: {
    keywords: string[];
    location_code?: number;
    language_code?: string;
    project_id?: string;
    target_url?: string;
    tag?: string;
    selected_stages?: string[];
  }) => api.post('/serp/batch-analyze', data),

  requests: (filters: { limit?: number; offset?: number; project_id?: string } = {}) => {
    const params = new URLSearchParams();
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.offset) params.set('offset', String(filters.offset));
    if (filters.project_id) params.set('project_id', filters.project_id);
    const qs = params.toString();
    return api.get<SerpRequest[]>(`/serp/requests${qs ? `?${qs}` : ''}`);
  },

  getRequest: (id: string) =>
    api.get<SerpRequestWithResults>(`/serp/requests/${id}`),

  exportExcel: (id: string) =>
    api.download(`/serp/requests/${id}/export`),

  exportProject: (projectId: string, options: {
    keyword?: string;
    format?: 'excel' | 'json';
    include_markdown?: boolean;
    include_html?: boolean;
    include_seo?: boolean;
  } = {}) => {
    const params = new URLSearchParams();
    if (options.keyword) params.set('keyword', options.keyword);
    if (options.format) params.set('format', options.format);
    if (options.include_markdown) params.set('include_markdown', 'true');
    if (options.include_html) params.set('include_html', 'true');
    if (options.include_seo) params.set('include_seo', 'true');
    const qs = params.toString();
    return api.download(`/serp/export/project/${projectId}${qs ? `?${qs}` : ''}`);
  },

  // Keep backward compat alias
  exportProjectExcel: (projectId: string, keyword?: string) => {
    const params = new URLSearchParams();
    if (keyword) params.set('keyword', keyword);
    const qs = params.toString();
    return api.download(`/serp/export/project/${projectId}${qs ? `?${qs}` : ''}`);
  },

  autoScrape: (id: string) =>
    api.post(`/serp/requests/${id}/auto-scrape`),
};
